import React, { useState, useEffect, useCallback } from 'react';
import {
  CalendarIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  PlusIcon,
  ClockIcon,
  CheckCircleIcon,
  XCircleIcon,
  ExclamationTriangleIcon,
  EyeIcon,
  PencilIcon,
  TrashIcon,
  XMarkIcon,
  DocumentTextIcon,
  RocketLaunchIcon,
  ArrowPathIcon,
  FunnelIcon,
  Squares2X2Icon,
  ListBulletIcon,
  EllipsisVerticalIcon,
  BellIcon,
  LinkIcon,
  GlobeAltIcon,
  EnvelopeIcon,
  DevicePhoneMobileIcon,
  MegaphoneIcon
} from '@heroicons/react/24/outline';
import api from '../services/api';

const styles = {
  container: {
    padding: '0',
    backgroundColor: 'transparent',
    minHeight: 'auto'
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '24px',
    flexWrap: 'wrap',
    gap: '16px'
  },
  title: {
    fontSize: '28px',
    fontWeight: '700',
    color: 'var(--text-primary)',
    margin: 0
  },
  subtitle: {
    fontSize: '14px',
    color: 'var(--text-secondary)',
    marginTop: '4px'
  },
  headerActions: {
    display: 'flex',
    gap: '12px',
    alignItems: 'center'
  },
  btn: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '8px',
    padding: '10px 16px',
    borderRadius: '8px',
    fontSize: '14px',
    fontWeight: '500',
    cursor: 'pointer',
    transition: 'all 0.2s',
    border: 'none'
  },
  btnPrimary: {
    backgroundColor: 'var(--accent-primary)',
    color: 'white'
  },
  btnSecondary: {
    backgroundColor: 'var(--bg-secondary)',
    color: 'var(--text-primary)',
    border: '1px solid var(--border-color)'
  },
  btnDanger: {
    backgroundColor: '#ef4444',
    color: 'white'
  },
  btnIcon: {
    padding: '8px',
    borderRadius: '6px',
    backgroundColor: 'var(--bg-secondary)',
    border: '1px solid var(--border-color)',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  },
  select: {
    padding: '10px 36px 10px 12px',
    borderRadius: '8px',
    border: '1px solid var(--border-color)',
    backgroundColor: 'var(--bg-secondary)',
    color: 'var(--text-primary)',
    fontSize: '14px',
    cursor: 'pointer',
    appearance: 'none',
    backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='%236b7280'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M19 9l-7 7-7-7'%3E%3C/path%3E%3C/svg%3E")`,
    backgroundRepeat: 'no-repeat',
    backgroundPosition: 'right 10px center',
    backgroundSize: '16px'
  },
  viewToggle: {
    display: 'flex',
    backgroundColor: 'var(--bg-secondary)',
    borderRadius: '6px',
    padding: '4px',
    border: '1px solid var(--border-color)'
  },
  viewToggleBtn: {
    padding: '6px 12px',
    borderRadius: '4px',
    cursor: 'pointer',
    backgroundColor: 'transparent',
    border: 'none',
    color: 'var(--text-secondary)',
    fontSize: '14px',
    display: 'flex',
    alignItems: 'center',
    gap: '6px'
  },
  viewToggleBtnActive: {
    backgroundColor: 'var(--accent-primary)',
    color: 'white'
  },
  calendarContainer: {
    backgroundColor: 'var(--bg-secondary)',
    borderRadius: '12px',
    border: '1px solid var(--border-color)',
    overflow: 'hidden'
  },
  calendarHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '16px 20px',
    borderBottom: '1px solid var(--border-color)',
    backgroundColor: 'var(--bg-primary)'
  },
  calendarNav: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px'
  },
  calendarMonth: {
    fontSize: '18px',
    fontWeight: '600',
    color: 'var(--text-primary)',
    minWidth: '180px',
    textAlign: 'center'
  },
  calendarGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(7, 1fr)'
  },
  dayHeader: {
    padding: '12px',
    textAlign: 'center',
    fontSize: '12px',
    fontWeight: '600',
    color: 'var(--text-secondary)',
    textTransform: 'uppercase',
    backgroundColor: 'var(--bg-primary)',
    borderBottom: '1px solid var(--border-color)'
  },
  dayCell: {
    minHeight: '120px',
    padding: '8px',
    borderRight: '1px solid var(--border-color)',
    borderBottom: '1px solid var(--border-color)',
    backgroundColor: 'var(--bg-secondary)',
    cursor: 'pointer',
    transition: 'background-color 0.2s',
    position: 'relative'
  },
  dayCellOtherMonth: {
    backgroundColor: 'var(--bg-primary)',
    opacity: 0.5
  },
  dayCellToday: {
    backgroundColor: 'rgba(var(--accent-primary-rgb), 0.05)'
  },
  dayCellSelected: {
    backgroundColor: 'rgba(var(--accent-primary-rgb), 0.1)'
  },
  dayNumber: {
    fontSize: '14px',
    fontWeight: '500',
    color: 'var(--text-primary)',
    marginBottom: '4px'
  },
  dayNumberToday: {
    backgroundColor: 'var(--accent-primary)',
    color: 'white',
    width: '28px',
    height: '28px',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  },
  eventPill: {
    padding: '4px 8px',
    borderRadius: '4px',
    fontSize: '11px',
    fontWeight: '500',
    marginBottom: '4px',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: '4px'
  },
  moreEvents: {
    fontSize: '11px',
    color: 'var(--text-secondary)',
    padding: '2px 8px',
    cursor: 'pointer'
  },
  sidebar: {
    backgroundColor: 'var(--bg-secondary)',
    borderRadius: '12px',
    border: '1px solid var(--border-color)',
    padding: '20px',
    height: 'fit-content'
  },
  sidebarTitle: {
    fontSize: '16px',
    fontWeight: '600',
    color: 'var(--text-primary)',
    marginBottom: '16px'
  },
  upcomingList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px'
  },
  upcomingItem: {
    padding: '12px',
    borderRadius: '8px',
    backgroundColor: 'var(--bg-primary)',
    border: '1px solid var(--border-color)',
    cursor: 'pointer',
    transition: 'all 0.2s'
  },
  upcomingItemHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    marginBottom: '8px'
  },
  upcomingItemTitle: {
    fontSize: '14px',
    fontWeight: '500',
    color: 'var(--text-primary)',
    flex: 1
  },
  upcomingItemTime: {
    fontSize: '12px',
    color: 'var(--text-secondary)',
    display: 'flex',
    alignItems: 'center',
    gap: '4px'
  },
  upcomingItemType: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '4px',
    padding: '2px 8px',
    borderRadius: '4px',
    fontSize: '11px',
    fontWeight: '500'
  },
  modal: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0,0,0,0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000
  },
  modalContent: {
    backgroundColor: 'var(--bg-primary)',
    borderRadius: '12px',
    width: '100%',
    maxWidth: '600px',
    maxHeight: '90vh',
    overflow: 'auto',
    boxShadow: '0 20px 60px rgba(0,0,0,0.3)'
  },
  modalHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '20px 24px',
    borderBottom: '1px solid var(--border-color)'
  },
  modalTitle: {
    fontSize: '18px',
    fontWeight: '600',
    color: 'var(--text-primary)',
    margin: 0
  },
  modalBody: {
    padding: '24px'
  },
  modalFooter: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '12px',
    padding: '16px 24px',
    borderTop: '1px solid var(--border-color)',
    backgroundColor: 'var(--bg-secondary)'
  },
  formGroup: {
    marginBottom: '20px'
  },
  label: {
    display: 'block',
    fontSize: '14px',
    fontWeight: '500',
    color: 'var(--text-primary)',
    marginBottom: '8px'
  },
  input: {
    width: '100%',
    padding: '10px 12px',
    borderRadius: '8px',
    border: '1px solid var(--border-color)',
    backgroundColor: 'var(--bg-secondary)',
    color: 'var(--text-primary)',
    fontSize: '14px',
    outline: 'none',
    transition: 'border-color 0.2s',
    boxSizing: 'border-box'
  },
  textarea: {
    width: '100%',
    padding: '10px 12px',
    borderRadius: '8px',
    border: '1px solid var(--border-color)',
    backgroundColor: 'var(--bg-secondary)',
    color: 'var(--text-primary)',
    fontSize: '14px',
    outline: 'none',
    minHeight: '100px',
    resize: 'vertical',
    boxSizing: 'border-box',
    fontFamily: 'inherit'
  },
  formRow: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '16px'
  },
  statusBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    padding: '4px 10px',
    borderRadius: '12px',
    fontSize: '12px',
    fontWeight: '500'
  },
  statusScheduled: {
    backgroundColor: 'rgba(59, 130, 246, 0.1)',
    color: '#3b82f6'
  },
  statusPublished: {
    backgroundColor: 'rgba(16, 185, 129, 0.1)',
    color: '#10b981'
  },
  statusDraft: {
    backgroundColor: 'rgba(107, 114, 128, 0.1)',
    color: '#6b7280'
  },
  statusFailed: {
    backgroundColor: 'rgba(239, 68, 68, 0.1)',
    color: '#ef4444'
  },
  typeColors: {
    'landing-page': { bg: '#6366f1', light: 'rgba(99, 102, 241, 0.1)' },
    'email': { bg: '#10b981', light: 'rgba(16, 185, 129, 0.1)' },
    'social': { bg: '#f59e0b', light: 'rgba(245, 158, 11, 0.1)' },
    'blog': { bg: '#3b82f6', light: 'rgba(59, 130, 246, 0.1)' },
    'sms': { bg: '#8b5cf6', light: 'rgba(139, 92, 246, 0.1)' },
    'push': { bg: '#ec4899', light: 'rgba(236, 72, 153, 0.1)' }
  },
  listView: {
    padding: '0'
  },
  listItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
    padding: '16px 20px',
    borderBottom: '1px solid var(--border-color)',
    cursor: 'pointer',
    transition: 'background-color 0.2s'
  },
  listItemHover: {
    backgroundColor: 'var(--bg-tertiary)'
  },
  listItemIcon: {
    width: '40px',
    height: '40px',
    borderRadius: '8px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  },
  listItemInfo: {
    flex: 1,
    minWidth: 0
  },
  listItemTitle: {
    fontSize: '14px',
    fontWeight: '500',
    color: 'var(--text-primary)',
    marginBottom: '4px'
  },
  listItemMeta: {
    fontSize: '12px',
    color: 'var(--text-secondary)',
    display: 'flex',
    gap: '12px'
  },
  listItemActions: {
    display: 'flex',
    gap: '8px'
  },
  emptyState: {
    textAlign: 'center',
    padding: '60px 20px',
    color: 'var(--text-secondary)'
  },
  emptyIcon: {
    width: '64px',
    height: '64px',
    margin: '0 auto 16px',
    opacity: 0.5
  },
  dropdown: {
    position: 'absolute',
    top: '100%',
    right: 0,
    backgroundColor: 'var(--bg-primary)',
    borderRadius: '8px',
    border: '1px solid var(--border-color)',
    boxShadow: '0 10px 40px rgba(0,0,0,0.2)',
    minWidth: '160px',
    zIndex: 100,
    overflow: 'hidden'
  },
  dropdownItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '10px 16px',
    fontSize: '14px',
    color: 'var(--text-primary)',
    cursor: 'pointer',
    transition: 'background-color 0.2s'
  },
  dropdownItemDanger: {
    color: '#ef4444'
  },
  miniCalendar: {
    marginBottom: '24px'
  },
  miniCalendarGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(7, 1fr)',
    gap: '4px'
  },
  miniCalendarDay: {
    aspectRatio: '1',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '12px',
    borderRadius: '4px',
    cursor: 'pointer'
  },
  miniCalendarDayHasEvent: {
    backgroundColor: 'var(--accent-primary)',
    color: 'white'
  },
  statsRow: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    gap: '12px',
    marginBottom: '24px'
  },
  statCard: {
    backgroundColor: 'var(--bg-primary)',
    borderRadius: '8px',
    padding: '16px',
    textAlign: 'center',
    border: '1px solid var(--border-color)'
  },
  statValue: {
    fontSize: '24px',
    fontWeight: '700',
    color: 'var(--text-primary)'
  },
  statLabel: {
    fontSize: '12px',
    color: 'var(--text-secondary)',
    marginTop: '4px'
  }
};

const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

const ScheduledDeploymentCalendar = ({ projectId }) => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(null);
  const [deployments, setDeployments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState('month');
  const [showModal, setShowModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedDeployment, setSelectedDeployment] = useState(null);
  const [filter, setFilter] = useState('all');
  const [hoveredItem, setHoveredItem] = useState(null);
  const [showDropdown, setShowDropdown] = useState(null);

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    type: 'landing-page',
    scheduled_date: '',
    scheduled_time: '',
    content_id: '',
    notify_before: '30',
    auto_publish: true
  });

  const fetchDeployments = useCallback(async () => {
    setLoading(true);
    try {
      const startOfMonth = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);
      const endOfMonth = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0);

      const response = await api.get('/content/scheduled-deployments', {
        params: {
          start_date: startOfMonth.toISOString(),
          end_date: endOfMonth.toISOString(),
          type: filter !== 'all' ? filter : undefined,
          project_id: projectId
        }
      });
      setDeployments(response.data || generateMockDeployments());
    } catch (error) {
      console.error('Error fetching deployments:', error);
      setDeployments(generateMockDeployments());
    } finally {
      setLoading(false);
    }
  }, [currentDate, filter, projectId]);

  useEffect(() => {
    fetchDeployments();
  }, [fetchDeployments]);

  const generateMockDeployments = () => {
    const today = new Date();
    return [
      { id: 1, title: 'Summer Sale Landing Page', type: 'landing-page', scheduled_at: new Date(today.getFullYear(), today.getMonth(), today.getDate() + 2, 10, 0).toISOString(), status: 'scheduled' },
      { id: 2, title: 'Welcome Email Campaign', type: 'email', scheduled_at: new Date(today.getFullYear(), today.getMonth(), today.getDate() + 2, 14, 30).toISOString(), status: 'scheduled' },
      { id: 3, title: 'Product Launch Blog', type: 'blog', scheduled_at: new Date(today.getFullYear(), today.getMonth(), today.getDate() + 5, 9, 0).toISOString(), status: 'scheduled' },
      { id: 4, title: 'Flash Sale SMS', type: 'sms', scheduled_at: new Date(today.getFullYear(), today.getMonth(), today.getDate() + 7, 12, 0).toISOString(), status: 'scheduled' },
      { id: 5, title: 'Instagram Campaign', type: 'social', scheduled_at: new Date(today.getFullYear(), today.getMonth(), today.getDate() + 3, 15, 0).toISOString(), status: 'scheduled' },
      { id: 6, title: 'Newsletter Q4', type: 'email', scheduled_at: new Date(today.getFullYear(), today.getMonth(), today.getDate() + 10, 8, 0).toISOString(), status: 'draft' },
      { id: 7, title: 'Holiday Push Notification', type: 'push', scheduled_at: new Date(today.getFullYear(), today.getMonth(), today.getDate() + 14, 11, 0).toISOString(), status: 'scheduled' },
      { id: 8, title: 'Case Study Page', type: 'landing-page', scheduled_at: new Date(today.getFullYear(), today.getMonth(), today.getDate() - 2, 10, 0).toISOString(), status: 'published' },
      { id: 9, title: 'Webinar Reminder', type: 'email', scheduled_at: new Date(today.getFullYear(), today.getMonth(), today.getDate() - 1, 9, 0).toISOString(), status: 'published' }
    ];
  };

  const getCalendarDays = () => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const startDate = new Date(firstDay);
    startDate.setDate(startDate.getDate() - firstDay.getDay());

    const days = [];
    const current = new Date(startDate);

    for (let i = 0; i < 42; i++) {
      days.push(new Date(current));
      current.setDate(current.getDate() + 1);
    }

    return days;
  };

  const getDeploymentsForDate = (date) => {
    return deployments.filter(d => {
      const deployDate = new Date(d.scheduled_at);
      return deployDate.toDateString() === date.toDateString();
    });
  };

  const isToday = (date) => {
    return date.toDateString() === new Date().toDateString();
  };

  const isCurrentMonth = (date) => {
    return date.getMonth() === currentDate.getMonth();
  };

  const navigateMonth = (direction) => {
    setCurrentDate(prev => {
      const newDate = new Date(prev);
      newDate.setMonth(prev.getMonth() + direction);
      return newDate;
    });
  };

  const goToToday = () => {
    setCurrentDate(new Date());
    setSelectedDate(new Date());
  };

  const getTypeIcon = (type) => {
    const icons = {
      'landing-page': GlobeAltIcon,
      'email': EnvelopeIcon,
      'social': MegaphoneIcon,
      'blog': DocumentTextIcon,
      'sms': DevicePhoneMobileIcon,
      'push': BellIcon
    };
    return icons[type] || DocumentTextIcon;
  };

  const getStatusStyle = (status) => {
    const statusStyles = {
      scheduled: styles.statusScheduled,
      published: styles.statusPublished,
      draft: styles.statusDraft,
      failed: styles.statusFailed
    };
    return { ...styles.statusBadge, ...statusStyles[status] };
  };

  const formatTime = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const handleSchedule = async () => {
    try {
      const scheduledAt = new Date(`${formData.scheduled_date}T${formData.scheduled_time}`);
      await api.post('/content/scheduled-deployments', {
        ...formData,
        scheduled_at: scheduledAt.toISOString(),
        project_id: projectId
      });
      setShowModal(false);
      setFormData({
        title: '',
        description: '',
        type: 'landing-page',
        scheduled_date: '',
        scheduled_time: '',
        content_id: '',
        notify_before: '30',
        auto_publish: true
      });
      fetchDeployments();
    } catch (error) {
      console.error('Error scheduling deployment:', error);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this scheduled deployment?')) return;
    try {
      await api.delete(`/content/scheduled-deployments/${id}`);
      setShowDetailModal(false);
      fetchDeployments();
    } catch (error) {
      console.error('Error deleting deployment:', error);
    }
  };

  const handlePublishNow = async (id) => {
    try {
      await api.post(`/content/scheduled-deployments/${id}/publish`);
      fetchDeployments();
    } catch (error) {
      console.error('Error publishing:', error);
    }
  };

  const upcomingDeployments = deployments
    .filter(d => new Date(d.scheduled_at) >= new Date() && d.status === 'scheduled')
    .sort((a, b) => new Date(a.scheduled_at) - new Date(b.scheduled_at))
    .slice(0, 5);

  const stats = {
    scheduled: deployments.filter(d => d.status === 'scheduled').length,
    published: deployments.filter(d => d.status === 'published').length,
    draft: deployments.filter(d => d.status === 'draft').length
  };

  const renderCalendarView = () => (
    <div style={styles.calendarGrid}>
      {DAYS.map(day => (
        <div key={day} style={styles.dayHeader}>{day}</div>
      ))}
      {getCalendarDays().map((date, index) => {
        const dayDeployments = getDeploymentsForDate(date);
        const isSelected = selectedDate && date.toDateString() === selectedDate.toDateString();

        return (
          <div
            key={index}
            style={{
              ...styles.dayCell,
              ...(!isCurrentMonth(date) ? styles.dayCellOtherMonth : {}),
              ...(isToday(date) ? styles.dayCellToday : {}),
              ...(isSelected ? styles.dayCellSelected : {})
            }}
            onClick={() => setSelectedDate(date)}
          >
            <div style={isToday(date) ? styles.dayNumberToday : styles.dayNumber}>
              {date.getDate()}
            </div>
            {dayDeployments.slice(0, 3).map(deployment => {
              const typeColor = styles.typeColors[deployment.type] || styles.typeColors['blog'];
              return (
                <div
                  key={deployment.id}
                  style={{
                    ...styles.eventPill,
                    backgroundColor: typeColor.light,
                    color: typeColor.bg
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelectedDeployment(deployment);
                    setShowDetailModal(true);
                  }}
                >
                  {React.createElement(getTypeIcon(deployment.type), { style: { width: '10px', height: '10px' } })}
                  {deployment.title}
                </div>
              );
            })}
            {dayDeployments.length > 3 && (
              <div style={styles.moreEvents}>+{dayDeployments.length - 3} more</div>
            )}
          </div>
        );
      })}
    </div>
  );

  const renderListView = () => {
    const sortedDeployments = [...deployments].sort((a, b) => new Date(a.scheduled_at) - new Date(b.scheduled_at));

    return (
      <div style={styles.listView}>
        {sortedDeployments.length === 0 ? (
          <div style={styles.emptyState}>
            <CalendarIcon style={styles.emptyIcon} />
            <p>No scheduled deployments found</p>
          </div>
        ) : (
          sortedDeployments.map(deployment => {
            const typeColor = styles.typeColors[deployment.type] || styles.typeColors['blog'];
            const TypeIcon = getTypeIcon(deployment.type);

            return (
              <div
                key={deployment.id}
                style={{
                  ...styles.listItem,
                  ...(hoveredItem === deployment.id ? styles.listItemHover : {})
                }}
                onMouseEnter={() => setHoveredItem(deployment.id)}
                onMouseLeave={() => setHoveredItem(null)}
                onClick={() => {
                  setSelectedDeployment(deployment);
                  setShowDetailModal(true);
                }}
              >
                <div style={{ ...styles.listItemIcon, backgroundColor: typeColor.light }}>
                  <TypeIcon style={{ width: '20px', height: '20px', color: typeColor.bg }} />
                </div>
                <div style={styles.listItemInfo}>
                  <div style={styles.listItemTitle}>{deployment.title}</div>
                  <div style={styles.listItemMeta}>
                    <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                      <CalendarIcon style={{ width: '12px', height: '12px' }} />
                      {formatDate(deployment.scheduled_at)}
                    </span>
                    <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                      <ClockIcon style={{ width: '12px', height: '12px' }} />
                      {formatTime(deployment.scheduled_at)}
                    </span>
                  </div>
                </div>
                <span style={getStatusStyle(deployment.status)}>
                  {deployment.status === 'scheduled' && <ClockIcon style={{ width: '12px', height: '12px' }} />}
                  {deployment.status === 'published' && <CheckCircleIcon style={{ width: '12px', height: '12px' }} />}
                  {deployment.status === 'draft' && <DocumentTextIcon style={{ width: '12px', height: '12px' }} />}
                  {deployment.status.charAt(0).toUpperCase() + deployment.status.slice(1)}
                </span>
                <div style={styles.listItemActions}>
                  <button
                    style={styles.btnIcon}
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedDeployment(deployment);
                      setShowDetailModal(true);
                    }}
                  >
                    <EyeIcon style={{ width: '16px', height: '16px', color: 'var(--text-secondary)' }} />
                  </button>
                  <div style={{ position: 'relative' }}>
                    <button
                      style={styles.btnIcon}
                      onClick={(e) => {
                        e.stopPropagation();
                        setShowDropdown(showDropdown === deployment.id ? null : deployment.id);
                      }}
                    >
                      <EllipsisVerticalIcon style={{ width: '16px', height: '16px', color: 'var(--text-secondary)' }} />
                    </button>
                    {showDropdown === deployment.id && (
                      <div style={styles.dropdown}>
                        <div
                          style={styles.dropdownItem}
                          onClick={(e) => {
                            e.stopPropagation();
                            handlePublishNow(deployment.id);
                            setShowDropdown(null);
                          }}
                        >
                          <RocketLaunchIcon style={{ width: '16px', height: '16px' }} />
                          Publish Now
                        </div>
                        <div
                          style={styles.dropdownItem}
                          onClick={(e) => {
                            e.stopPropagation();
                            // Edit logic
                            setShowDropdown(null);
                          }}
                        >
                          <PencilIcon style={{ width: '16px', height: '16px' }} />
                          Edit
                        </div>
                        <div
                          style={{ ...styles.dropdownItem, ...styles.dropdownItemDanger }}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDelete(deployment.id);
                            setShowDropdown(null);
                          }}
                        >
                          <TrashIcon style={{ width: '16px', height: '16px' }} />
                          Delete
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>
    );
  };

  return (
    <div style={styles.container}>
      {/* Filter and Actions Bar */}
      <div style={styles.header}>
        <div style={styles.headerActions}>
          <select
            style={styles.select}
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
          >
            <option value="all">All Types</option>
            <option value="landing-page">Landing Pages</option>
            <option value="email">Email</option>
            <option value="social">Social</option>
            <option value="blog">Blog</option>
            <option value="sms">SMS</option>
            <option value="push">Push</option>
          </select>
          <div style={styles.viewToggle}>
            <button
              style={{
                ...styles.viewToggleBtn,
                ...(viewMode === 'month' ? styles.viewToggleBtnActive : {})
              }}
              onClick={() => setViewMode('month')}
            >
              <Squares2X2Icon style={{ width: '16px', height: '16px' }} />
              Calendar
            </button>
            <button
              style={{
                ...styles.viewToggleBtn,
                ...(viewMode === 'list' ? styles.viewToggleBtnActive : {})
              }}
              onClick={() => setViewMode('list')}
            >
              <ListBulletIcon style={{ width: '16px', height: '16px' }} />
              List
            </button>
          </div>
          <button style={styles.btnIcon} onClick={fetchDeployments}>
            <ArrowPathIcon style={{ width: '18px', height: '18px', color: 'var(--text-secondary)' }} />
          </button>
          <button
            style={{ ...styles.btn, ...styles.btnPrimary }}
            onClick={() => setShowModal(true)}
          >
            <PlusIcon style={{ width: '18px', height: '18px' }} />
            Schedule Deployment
          </button>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 320px', gap: '24px' }}>
        {/* Main Calendar/List */}
        <div style={styles.calendarContainer}>
          <div style={styles.calendarHeader}>
            <div style={styles.calendarNav}>
              <button style={styles.btnIcon} onClick={() => navigateMonth(-1)}>
                <ChevronLeftIcon style={{ width: '18px', height: '18px', color: 'var(--text-secondary)' }} />
              </button>
              <span style={styles.calendarMonth}>
                {MONTHS[currentDate.getMonth()]} {currentDate.getFullYear()}
              </span>
              <button style={styles.btnIcon} onClick={() => navigateMonth(1)}>
                <ChevronRightIcon style={{ width: '18px', height: '18px', color: 'var(--text-secondary)' }} />
              </button>
            </div>
            <button style={{ ...styles.btn, ...styles.btnSecondary }} onClick={goToToday}>
              Today
            </button>
          </div>

          {loading ? (
            <div style={styles.emptyState}>
              <ArrowPathIcon style={{ ...styles.emptyIcon, animation: 'spin 1s linear infinite' }} />
              <p>Loading...</p>
            </div>
          ) : viewMode === 'month' ? renderCalendarView() : renderListView()}
        </div>

        {/* Sidebar */}
        <div style={styles.sidebar}>
          <div style={styles.statsRow}>
            <div style={styles.statCard}>
              <div style={styles.statValue}>{stats.scheduled}</div>
              <div style={styles.statLabel}>Scheduled</div>
            </div>
            <div style={styles.statCard}>
              <div style={styles.statValue}>{stats.published}</div>
              <div style={styles.statLabel}>Published</div>
            </div>
            <div style={styles.statCard}>
              <div style={styles.statValue}>{stats.draft}</div>
              <div style={styles.statLabel}>Drafts</div>
            </div>
          </div>

          <div style={styles.sidebarTitle}>Upcoming Deployments</div>
          <div style={styles.upcomingList}>
            {upcomingDeployments.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '20px', color: 'var(--text-secondary)', fontSize: '14px' }}>
                No upcoming deployments
              </div>
            ) : (
              upcomingDeployments.map(deployment => {
                const typeColor = styles.typeColors[deployment.type] || styles.typeColors['blog'];
                const TypeIcon = getTypeIcon(deployment.type);

                return (
                  <div
                    key={deployment.id}
                    style={styles.upcomingItem}
                    onClick={() => {
                      setSelectedDeployment(deployment);
                      setShowDetailModal(true);
                    }}
                  >
                    <div style={styles.upcomingItemHeader}>
                      <span style={{ ...styles.upcomingItemType, backgroundColor: typeColor.light, color: typeColor.bg }}>
                        <TypeIcon style={{ width: '12px', height: '12px' }} />
                        {deployment.type}
                      </span>
                    </div>
                    <div style={styles.upcomingItemTitle}>{deployment.title}</div>
                    <div style={styles.upcomingItemTime}>
                      <CalendarIcon style={{ width: '12px', height: '12px' }} />
                      {formatDate(deployment.scheduled_at)} at {formatTime(deployment.scheduled_at)}
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>
      </div>

      {/* Schedule Modal */}
      {showModal && (
        <div style={styles.modal} onClick={() => setShowModal(false)}>
          <div style={styles.modalContent} onClick={e => e.stopPropagation()}>
            <div style={styles.modalHeader}>
              <h3 style={styles.modalTitle}>Schedule Deployment</h3>
              <button style={styles.btnIcon} onClick={() => setShowModal(false)}>
                <XMarkIcon style={{ width: '20px', height: '20px', color: 'var(--text-secondary)' }} />
              </button>
            </div>
            <div style={styles.modalBody}>
              <div style={styles.formGroup}>
                <label style={styles.label}>Title</label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  placeholder="Enter deployment title"
                  style={styles.input}
                />
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>Description</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Enter description..."
                  style={styles.textarea}
                />
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>Content Type</label>
                <select
                  value={formData.type}
                  onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                  style={styles.select}
                >
                  <option value="landing-page">Landing Page</option>
                  <option value="email">Email Campaign</option>
                  <option value="social">Social Media</option>
                  <option value="blog">Blog Post</option>
                  <option value="sms">SMS</option>
                  <option value="push">Push Notification</option>
                </select>
              </div>

              <div style={styles.formRow}>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Date</label>
                  <input
                    type="date"
                    value={formData.scheduled_date}
                    onChange={(e) => setFormData({ ...formData, scheduled_date: e.target.value })}
                    style={styles.input}
                  />
                </div>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Time</label>
                  <input
                    type="time"
                    value={formData.scheduled_time}
                    onChange={(e) => setFormData({ ...formData, scheduled_time: e.target.value })}
                    style={styles.input}
                  />
                </div>
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>Notify Before (minutes)</label>
                <select
                  value={formData.notify_before}
                  onChange={(e) => setFormData({ ...formData, notify_before: e.target.value })}
                  style={styles.select}
                >
                  <option value="15">15 minutes</option>
                  <option value="30">30 minutes</option>
                  <option value="60">1 hour</option>
                  <option value="120">2 hours</option>
                  <option value="1440">1 day</option>
                </select>
              </div>

              <div style={styles.formGroup}>
                <label style={{ ...styles.label, display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={formData.auto_publish}
                    onChange={(e) => setFormData({ ...formData, auto_publish: e.target.checked })}
                  />
                  Auto-publish at scheduled time
                </label>
              </div>
            </div>
            <div style={styles.modalFooter}>
              <button style={{ ...styles.btn, ...styles.btnSecondary }} onClick={() => setShowModal(false)}>
                Cancel
              </button>
              <button
                style={{ ...styles.btn, ...styles.btnPrimary }}
                onClick={handleSchedule}
                disabled={!formData.title || !formData.scheduled_date || !formData.scheduled_time}
              >
                <CalendarIcon style={{ width: '16px', height: '16px' }} />
                Schedule
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Detail Modal */}
      {showDetailModal && selectedDeployment && (
        <div style={styles.modal} onClick={() => setShowDetailModal(false)}>
          <div style={styles.modalContent} onClick={e => e.stopPropagation()}>
            <div style={styles.modalHeader}>
              <h3 style={styles.modalTitle}>{selectedDeployment.title}</h3>
              <button style={styles.btnIcon} onClick={() => setShowDetailModal(false)}>
                <XMarkIcon style={{ width: '20px', height: '20px', color: 'var(--text-secondary)' }} />
              </button>
            </div>
            <div style={styles.modalBody}>
              <div style={{ display: 'flex', gap: '12px', marginBottom: '20px' }}>
                <span style={{ ...styles.upcomingItemType, backgroundColor: styles.typeColors[selectedDeployment.type]?.light, color: styles.typeColors[selectedDeployment.type]?.bg }}>
                  {React.createElement(getTypeIcon(selectedDeployment.type), { style: { width: '14px', height: '14px' } })}
                  {selectedDeployment.type}
                </span>
                <span style={getStatusStyle(selectedDeployment.status)}>
                  {selectedDeployment.status.charAt(0).toUpperCase() + selectedDeployment.status.slice(1)}
                </span>
              </div>

              <div style={{ marginBottom: '16px' }}>
                <div style={{ fontSize: '12px', color: 'var(--text-secondary)', marginBottom: '4px' }}>Scheduled For</div>
                <div style={{ fontSize: '16px', fontWeight: '500', color: 'var(--text-primary)' }}>
                  {formatDate(selectedDeployment.scheduled_at)} at {formatTime(selectedDeployment.scheduled_at)}
                </div>
              </div>

              {selectedDeployment.description && (
                <div style={{ marginBottom: '16px' }}>
                  <div style={{ fontSize: '12px', color: 'var(--text-secondary)', marginBottom: '4px' }}>Description</div>
                  <div style={{ fontSize: '14px', color: 'var(--text-primary)' }}>{selectedDeployment.description}</div>
                </div>
              )}
            </div>
            <div style={styles.modalFooter}>
              <button
                style={{ ...styles.btn, ...styles.btnDanger }}
                onClick={() => handleDelete(selectedDeployment.id)}
              >
                <TrashIcon style={{ width: '16px', height: '16px' }} />
                Delete
              </button>
              <button style={{ ...styles.btn, ...styles.btnSecondary }} onClick={() => setShowDetailModal(false)}>
                Close
              </button>
              {selectedDeployment.status === 'scheduled' && (
                <button
                  style={{ ...styles.btn, ...styles.btnPrimary }}
                  onClick={() => handlePublishNow(selectedDeployment.id)}
                >
                  <RocketLaunchIcon style={{ width: '16px', height: '16px' }} />
                  Publish Now
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ScheduledDeploymentCalendar;
